import { useEffect, useState } from 'react'
import { Button, Card, Form, Input, Modal, Select, Space, Table, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, formatApiError } from '../../api'
import { PageHeader, formatDateOrDash } from '../../components/ui'

type SyncJob = {
  id: number
  dataset_id: number
  name: string
  target_type: string
  status: string
  last_run_at: string | null
  last_error: string
}

type DatasetOpt = { id: number; name: string }

export default function DatasetSyncJobs() {
  const [jobs, setJobs] = useState<SyncJob[]>([])
  const [datasets, setDatasets] = useState<DatasetOpt[]>([])
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  const load = () => {
    adminApi
      .get<{ items: SyncJob[] }>('/admin/dataset-sync/jobs')
      .then((r) => setJobs(r.data.items || []))
      .catch(() => {})
  }

  useEffect(() => {
    load()
    adminApi
      .get<{ items: DatasetOpt[] }>('/admin/datasets/options', { params: { published_only: true } })
      .then((r) => setDatasets(r.data.items || []))
      .catch(() => {})
  }, [])

  const createJob = () => {
    void form.validateFields().then((v) => {
      adminApi
        .post('/admin/dataset-sync/jobs', {
          dataset_id: v.dataset_id,
          name: v.name,
          target_type: v.target_type,
          config_json: { url: v.url },
        })
        .then(() => {
          message.success('已创建')
          setOpen(false)
          form.resetFields()
          load()
        })
        .catch((e) => message.error(formatApiError(e, '创建失败')))
    })
  }

  const runJob = (id: number) => {
    adminApi
      .post(`/admin/dataset-sync/jobs/${id}/run`)
      .then((r) => {
        if (r.data.status === 'error') {
          message.error(r.data.last_error || '同步失败')
        } else {
          message.success('同步完成')
        }
        load()
      })
      .catch((e) => message.error(formatApiError(e, '执行失败')))
  }

  return (
    <div>
      <PageHeader
        title="同步任务"
        description="将数据集切片导出为 JSONL 并推送到 Webhook / Dify / FastGPT 等外部平台。"
      />
      <Card>
        <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 16 }} onClick={() => setOpen(true)}>
          新建任务
        </Button>
        <Table<SyncJob>
          rowKey="id"
          dataSource={jobs}
          pagination={false}
          columns={[
            { title: '名称', dataIndex: 'name' },
            { title: '数据集 ID', dataIndex: 'dataset_id', width: 96 },
            { title: '类型', dataIndex: 'target_type', width: 100 },
            { title: '状态', dataIndex: 'status', width: 88 },
            { title: '上次运行', dataIndex: 'last_run_at', width: 180, render: (v: string | null) => formatDateOrDash(v) },
            { title: '错误', dataIndex: 'last_error', ellipsis: true },
            {
              title: '操作',
              width: 120,
              render: (_, r) => (
                <Space>
                  <Button size="small" onClick={() => runJob(r.id)}>
                    立即同步
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() =>
                      adminApi.delete(`/admin/dataset-sync/jobs/${r.id}`).then(() => load())
                    }
                  >
                    删
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal title="新建同步任务" open={open} onCancel={() => setOpen(false)} onOk={createJob}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="任务名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="dataset_id" label="数据集" rules={[{ required: true }]}>
            <Select options={datasets.map((d) => ({ value: d.id, label: d.name }))} />
          </Form.Item>
          <Form.Item name="target_type" label="目标类型" initialValue="webhook" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'webhook', label: 'Webhook (NDJSON)' },
                { value: 'dify', label: 'Dify' },
                { value: 'fastgpt', label: 'FastGPT' },
                { value: 'generic', label: '通用 JSON' },
              ]}
            />
          </Form.Item>
          <Form.Item name="url" label="目标 URL" rules={[{ required: true }]}>
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
